/**
 * Tree data manipulation utilities
 * Helpers for working with hierarchical tree structures
 */

/**
 * Build tree data from flat array of items
 * @param {Array} items - Flat array of items with project, target, batch properties
 * @returns {Array} - Hierarchical tree structure
 */
export const buildTreeFromItems = (items) => {
  const tree = {};
  
  items.forEach(item => {
    const project = item.project || 'Unknown';
    const target = item.target || 'Unknown';
    const batch = item.batch || 'Unknown';
    
    if (!tree[project]) {
      tree[project] = {
        id: `project-${project}`,
        label: project,
        type: 'project',
        children: {}
      };
    }
    
    if (!tree[project].children[target]) {
      tree[project].children[target] = {
        id: `target-${project}-${target}`,
        label: target,
        type: 'target',
        project,
        children: {}
      };
    }
    
    if (!tree[project].children[target].children[batch]) {
      tree[project].children[target].children[batch] = {
        id: `batch-${project}-${target}-${batch}`,
        label: batch,
        type: 'batch',
        project,
        target,
        count: 0
      };
    }
    
    tree[project].children[target].children[batch].count++;
  });
  
  // Convert to array format
  return Object.values(tree).map(project => ({
    ...project,
    children: Object.values(project.children).map(target => ({
      ...target,
      children: Object.values(target.children)
    }))
  }));
};

/**
 * Find node in tree by ID
 * @param {Array} tree - Tree structure
 * @param {string} nodeId - Node ID to find
 * @returns {Object|null} - Found node or null
 */
export const findNodeById = (tree, nodeId) => {
  for (const node of tree) {
    if (node.id === nodeId) return node;
    if (node.children) {
      const found = findNodeById(node.children, nodeId);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Get all parent IDs for a node
 * @param {string} nodeId - Node ID with :: separator
 * @returns {Array} - Array of parent IDs
 */
export const getParentIds = (nodeId) => {
  const parts = nodeId.split('::');
  const parents = [];
  
  if (parts[0] === 'batch' && parts.length >= 4) {
    // batch::project::target::batch
    parents.push(`target::${parts[1]}::${parts[2]}`);
    parents.push(`project::${parts[1]}`);
  } else if (parts[0] === 'target' && parts.length >= 3) {
    // target::project::target
    parents.push(`project::${parts[1]}`);
  }
  
  return parents;
};

/**
 * Parse node ID to get metadata
 * @param {string} nodeId - Node ID with :: separator
 * @returns {Object} - Parsed metadata
 */
export const parseNodeId = (nodeId) => {
  const parts = nodeId.split('::');
  const type = parts[0];
  
  if (type === 'project') {
    return { type, project: parts[1] };
  } else if (type === 'target') {
    return { 
      type, 
      project: parts[1], 
      target: parts[2]
    };
  } else if (type === 'batch') {
    return { 
      type, 
      project: parts[1], 
      target: parts[2],
      batch: parts[3]
    };
  }
  
  return { type: 'unknown' };
};

/**
 * Filter tree based on search term
 * @param {Array} tree - Tree structure
 * @param {string} searchTerm - Search term
 * @returns {Array} - Filtered tree
 */
export const filterTree = (tree, searchTerm) => {
  if (!searchTerm) return tree;
  
  const term = searchTerm.toLowerCase();
  
  return tree.reduce((filtered, node) => {
    const matches = node.label.toLowerCase().includes(term);
    const filteredChildren = node.children ? filterTree(node.children, searchTerm) : [];
    
    if (matches || filteredChildren.length > 0) {
      filtered.push({
        ...node,
        children: filteredChildren
      });
    }
    
    return filtered;
  }, []);
};

/**
 * Get all descendant IDs for a node
 * @param {Object} node - Tree node
 * @returns {Array} - Array of descendant IDs
 */
export const getDescendantIds = (node) => {
  const ids = [];
  
  const traverse = (n) => {
    ids.push(n.id);
    if (n.children) {
      n.children.forEach(child => traverse(child));
    }
  };
  
  if (node.children) {
    node.children.forEach(child => traverse(child));
  }
  
  return ids;
};